import {
  CSSProperties,
  MaybeRefOrGetter,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  toValue,
} from 'vue';
import { calculatePopupPosition } from '@/common/utils/browser';

export const useDateTimePicker = (useQuickDate: MaybeRefOrGetter<boolean>) => {
  const isShowQuickDatePopup = ref<boolean>(false);
  const showCalendar = ref<boolean>(false);
  const timeCalendarRef = ref();
  const dateTimePickerInputRef = ref();
  const dateTimePickerPopupRef = ref();
  const popupWrapperStyle = reactive<CSSProperties>({
    top: 'auto',
    bottom: 'auto',
    left: 'auto',
    right: 'auto',
    flexDirection: 'row',
    alignItems: 'flex-start',
  });

  const setPopupStyle = () => {
    const inputRect = dateTimePickerInputRef.value?.getBoundingClientRect();
    const popupRect = (
      dateTimePickerPopupRef.value?.$el ?? dateTimePickerPopupRef.value
    )?.getBoundingClientRect();
    if (!inputRect || !popupRect) return;

    const { top, bottom, left, right } = calculatePopupPosition(inputRect, popupRect, {
      width: window.innerWidth,
      height: window.innerHeight,
    });

    popupWrapperStyle.top = top;
    popupWrapperStyle.bottom = bottom;
    popupWrapperStyle.left = left;
    popupWrapperStyle.right = right;
  };

  const openQuickDatePopup = async () => {
    isShowQuickDatePopup.value = true;
    await nextTick();
    setPopupStyle();
  };

  const closeQuickDatePopup = () => {
    isShowQuickDatePopup.value = false;
  };

  const openCalendar = async () => {
    showCalendar.value = true;
    await nextTick();

    const inputRect = dateTimePickerInputRef.value?.getBoundingClientRect();

    const calendarRect = timeCalendarRef.value.$el?.getBoundingClientRect();
    if (!inputRect || !calendarRect) return;

    const windowSize = {
      width: window.innerWidth,
      height: window.innerHeight,
    };

    const { top, bottom, left, right } = calculatePopupPosition(
      inputRect,
      calendarRect,
      windowSize,
    );

    if (!isShowQuickDatePopup.value) {
      popupWrapperStyle.top = top;
      popupWrapperStyle.bottom = bottom;
      popupWrapperStyle.left = left;
      popupWrapperStyle.right = right;
    } else {
      const popupRect = (
        dateTimePickerPopupRef.value?.$el ?? dateTimePickerPopupRef.value
      )?.getBoundingClientRect();

      if (bottom !== 'auto') {
        if (popupWrapperStyle.bottom === 'auto' && popupRect) {
          popupWrapperStyle.top = `${
            inputRect.top + inputRect.height + popupRect.height - calendarRect.height
          }px`;
        }
        popupWrapperStyle.alignItems = 'flex-end';
      }

      if (inputRect.left + popupRect.width + calendarRect.width > windowSize.width) {
        popupWrapperStyle.flexDirection = 'row-reverse';
        popupWrapperStyle.right = '10px';
        popupWrapperStyle.left = 'auto';
      }
    }
  };

  const closeCalendar = async () => {
    showCalendar.value = false;
  };

  const closeAll = (e?: Event) => {
    if (e?.target && dateTimePickerInputRef.value?.contains(e.target as Node)) {
      return;
    }
    closeQuickDatePopup();
    closeCalendar();
  };

  const onScroll = (event: Event) => {
    if (!showCalendar.value && !isShowQuickDatePopup.value) {
      return;
    }

    const target = event.target as HTMLElement;
    const calendarEl = timeCalendarRef.value?.$el || timeCalendarRef.value;

    if (calendarEl && calendarEl.contains(target)) {
      return;
    }

    closeAll();
  };

  const togglePopup = () => {
    if (isShowQuickDatePopup.value || showCalendar.value) {
      closeQuickDatePopup();
      closeCalendar();
      return;
    }

    if (toValue(useQuickDate)) {
      openQuickDatePopup();
    } else {
      openCalendar();
    }
  };

  onMounted(() => {
    window.addEventListener('scroll', onScroll, true);
  });

  onBeforeUnmount(() => {
    window.removeEventListener('scroll', onScroll, true);
  });

  return {
    timeCalendarRef,
    dateTimePickerInputRef,
    dateTimePickerPopupRef,
    isShowQuickDatePopup,
    popupWrapperStyle,
    showCalendar,
    closeCalendar,
    openQuickDatePopup,
    openCalendar,
    closeAll,
    togglePopup,
  };
};
